import React from "react";
import Draggable from "react-draggable";
import { Maximize2, X } from "lucide-react";

const PickerMinimap = ({
    imageUrl,
    isMinimapFull, setIsMinimapFull,
    isMobile,
    minimapContainerW, minimapContainerH,
    minimapImageW, minimapImageH,
    dotShortX, dotShortY, dotLongX, dotLongY,
    boundsShort, boundsLong,
    allUsedAreas, restrictedAreas, preSelectedAreas,
    cutOption, preSelectedSize,
    hasFreeMovePurchase,
    trueWidth, imgDisplayHeight, yScale,
    vWidth, vShort, vLong,
    activePart, setActivePart,
    isDraggingMain, setIsDraggingMain,
    isConfirmed, isOrderFinished, isConfigOpen,
    collisionType,
    isFreeMoveLocked, isAllPreselectedUsed,
    borderColor,
    handleMinimapDrag,
    handleMiniScroll,
    minimapShortDotRef, minimapLongDotRef, minimapScrollRef,
    t,
}) => (
    <div
        key={isMinimapFull ? 'full' : 'corner'}
        className="minimap-wrapper"
        style={{
            position: 'absolute',
            top: isMinimapFull ? '50%' : 'auto',
            left: isMinimapFull ? '50%' : 'auto',
            bottom: isMinimapFull ? 'auto' : (isMobile ? '110px' : '24px'),
            right: isMinimapFull ? 'auto' : (isMobile ? '16px' : '32px'),
            transform: isMinimapFull ? 'translate(-50%, -50%)' : 'none',
            width: `${minimapContainerW}px`, height: `${minimapContainerH}px`,
            backgroundColor: '#fff', border: '3px solid #1A110B', borderRadius: isMinimapFull ? 0 : '12px', overflow: 'hidden',
            boxShadow: isMinimapFull ? '0 30px 80px rgba(0,0,0,0.6)' : '0 10px 30px rgba(0,0,0,0.35)',
            zIndex: isMinimapFull ? 1060 : 80,
            animation: isMinimapFull ? 'minimapPopFull 0.3s ease-out forwards' : 'minimapPopCorner 0.3s ease-out forwards'
        }}
    >
        <button
            onClick={() => setIsMinimapFull(!isMinimapFull)}
            className="position-absolute btn btn-sm rounded-circle shadow-sm"
            style={{ top: '8px', right: '8px', backgroundColor: '#1A110B', zIndex: 10, width: '28px', height: '28px', display: 'flex', justifyContent: 'center', alignItems: 'center', color: '#FFF', border: 'none' }}
        >
            {isMinimapFull ? <X size={12} /> : <Maximize2 size={12} />}
        </button>

        <div ref={minimapScrollRef} onScroll={handleMiniScroll} style={{ width: '100%', height: '100%', overflow: isMinimapFull ? 'auto' : 'hidden', position: 'relative' }}>
            <div style={{ width: `${minimapImageW}px`, height: `${minimapImageH}px`, position: 'relative' }}>
                <img src={imageUrl} alt="Minimap" decoding="async" style={{ width: '100%', height: '100%', display: 'block', objectFit: 'fill', opacity: 0.95, pointerEvents: 'none' }} crossOrigin="anonymous" />

                {allUsedAreas.map((area, idx) => {
                    let wRatio, hRatio, yRatio;
                    if (area.endType) {
                        wRatio = imgDisplayHeight > 0 ? vWidth / trueWidth : 0;
                        const displayH = area.endType === 'short' ? vShort : vLong;
                        hRatio = imgDisplayHeight > 0 ? displayH / imgDisplayHeight : 0;
                        yRatio = imgDisplayHeight > 0 ? (area.y * yScale) / imgDisplayHeight : 0;
                    } else {
                        wRatio = trueWidth > 0 ? area.w / trueWidth : 0;
                        hRatio = area.isFreeMove
                            ? (imgDisplayHeight > 0 ? area.h / imgDisplayHeight : 0)
                            : (imgDisplayHeight > 0 ? area.h / imgDisplayHeight : 0);
                        yRatio = area.isFreeMove
                            ? (imgDisplayHeight > 0 ? (area.y * yScale) / imgDisplayHeight : 0)
                            : (imgDisplayHeight > 0 ? (area.y * yScale) / imgDisplayHeight : 0);
                    }
                    return (
                        <div key={`mini-used-${idx}`} style={{ position: 'absolute', left: `${(area.x / trueWidth) * 100}%`, top: `${yRatio * 100}%`, width: `${wRatio * 100}%`, height: `${hRatio * 100}%`, backgroundColor: 'rgba(220, 53, 69, 0.5)', border: '1px solid #dc3545', pointerEvents: 'none' }} />
                    );
                })}

                {cutOption === 'free' && restrictedAreas.map((rect, idx) => (
                    <div key={`mini-rest-${idx}`} style={{ position: 'absolute', left: `${rect.x * 100}%`, top: `${rect.y * 100}%`, width: `${rect.w * 100}%`, height: `${rect.h * 100}%`, backgroundColor: 'rgba(220, 53, 69, 0.45)', border: '1px dashed #dc3545', pointerEvents: 'none' }}></div>
                ))}

                {cutOption === 'preselected' && preSelectedAreas.map((group, idx) => {
                    const isUsed = allUsedAreas.some(u => u.id && u.id.startsWith(group.uniqueAreaId));
                    const isSelected = preSelectedSize?.slotIndex === idx;
                    if (isUsed || hasFreeMovePurchase) return null;
                    const gold = '#C9A84C';
                    return (
                        <React.Fragment key={`mini-pre-${idx}`}>
                            <div style={{ position: 'absolute', left: `${group.longEnd.x * 100}%`, top: `${group.longEnd.y * 100}%`, width: `${group.longEnd.width * 100}%`, height: `${group.longEnd.height * 100}%`, backgroundColor: isSelected ? 'rgba(201,168,76,0.35)' : 'rgba(201,168,76,0.12)', border: `1px solid ${isSelected ? gold : 'rgba(201,168,76,0.5)'}`, pointerEvents: 'none' }} />
                            <div style={{ position: 'absolute', left: `${group.shortEnd.x * 100}%`, top: `${group.shortEnd.y * 100}%`, width: `${group.shortEnd.width * 100}%`, height: `${group.shortEnd.height * 100}%`, backgroundColor: isSelected ? 'rgba(201,168,76,0.2)' : 'rgba(201,168,76,0.06)', border: `1px solid ${isSelected ? gold : 'rgba(201,168,76,0.3)'}`, pointerEvents: 'none' }} />
                        </React.Fragment>
                    );
                })}

                <Draggable
                    nodeRef={minimapShortDotRef}
                    bounds={boundsShort}
                    position={{ x: dotShortX, y: dotShortY }}
                    onStart={() => { setIsDraggingMain(true); setActivePart('short'); }}
                    onDrag={(e, data) => handleMinimapDrag(data, 'short')}
                    onStop={() => setIsDraggingMain(false)}
                    disabled={isConfirmed || isOrderFinished || isConfigOpen || cutOption === 'preselected' || isFreeMoveLocked || isAllPreselectedUsed}
                >
                    <div ref={minimapShortDotRef} style={{ position: 'absolute', top: 0, left: 0, width: '24px', height: '24px', borderRadius: '50%', border: activePart === 'short' ? '2px solid #fff' : '2px solid #1A110B', backgroundColor: activePart === 'short' ? borderColor : '#fff', color: activePart === 'short' ? '#fff' : '#1A110B', cursor: (isConfirmed || isOrderFinished || isConfigOpen || cutOption === 'preselected' || isFreeMoveLocked || isAllPreselectedUsed) ? 'default' : 'grab', transition: isDraggingMain ? 'none' : 'background-color 0.2s ease', boxShadow: collisionType === 'SHORT STRAP OVERLAP' || collisionType === 'OVERLAP DETECTED' || collisionType === 'STRAPS OVERLAP' ? '0 0 0 6px rgba(220, 53, 69, 0.35)' : '0 2px 8px rgba(0,0,0,0.8)', display: (isFreeMoveLocked || isAllPreselectedUsed) ? 'none' : 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 'bold', zIndex: activePart === 'short' ? 101 : 100 }}>S</div>
                </Draggable>
                <Draggable
                    nodeRef={minimapLongDotRef}
                    bounds={boundsLong}
                    position={{ x: dotLongX, y: dotLongY }}
                    onStart={() => { setIsDraggingMain(true); setActivePart('long'); }}
                    onDrag={(e, data) => handleMinimapDrag(data, 'long')}
                    onStop={() => setIsDraggingMain(false)}
                    disabled={isConfirmed || isOrderFinished || isConfigOpen || cutOption === 'preselected' || isFreeMoveLocked || isAllPreselectedUsed}
                >
                    <div ref={minimapLongDotRef} style={{ position: 'absolute', top: 0, left: 0, width: '24px', height: '24px', borderRadius: '50%', border: activePart === 'long' ? '2px solid #fff' : '2px solid #1A110B', backgroundColor: activePart === 'long' ? borderColor : '#fff', color: activePart === 'long' ? '#fff' : '#1A110B', cursor: (isConfirmed || isOrderFinished || isConfigOpen || cutOption === 'preselected' || isFreeMoveLocked || isAllPreselectedUsed) ? 'default' : 'grab', transition: isDraggingMain ? 'none' : 'background-color 0.2s ease', boxShadow: collisionType === 'LONG STRAP OVERLAP' || collisionType === 'OVERLAP DETECTED' || collisionType === 'STRAPS OVERLAP' ? '0 0 0 6px rgba(220, 53, 69, 0.35)' : '0 2px 8px rgba(0,0,0,0.8)', display: (isFreeMoveLocked || isAllPreselectedUsed) ? 'none' : 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 'bold', zIndex: activePart === 'long' ? 101 : 100 }}>L</div>
                </Draggable>
            </div>
        </div>
    </div>
);

export default PickerMinimap;
